import { useEffect, useState } from "react";
import { Button, Modal } from "@/ui/atoms";

// Amount mapping formula: (LeftCol)+(RightCol) or (LeftCol)-(RightCol).
//
// Operand names may contain brackets: "\", "(" and ")" are backslash-escaped
// inside each operand, so the column "Amount (USD)" is stored as
// "(Amount \(USD\))-(Credit)". Only *unescaped* brackets delimit the operands,
// which keeps the grammar unambiguous. Formulas saved before escaping existed
// ("(Debit)-(Credit)") contain no backslashes and parse unchanged.
// Keep in sync with _AMOUNT_FORMULA_RE in the backend column_mapper.py.
const FORMULA_REGEX = /^\(((?:\\.|[^\\()])+)\)([+-])\(((?:\\.|[^\\()])+)\)$/;

function escapeOperand(name: string): string {
    return name.replace(/[\\()]/g, "\\$&");
}

function unescapeOperand(operand: string): string {
    return operand.replace(/\\(.)/g, "$1");
}

export function parseFormulaExpression(
    columnName: string,
): { left: string; op: "+" | "-"; right: string } | null {
    const m = columnName.match(FORMULA_REGEX);
    if (!m) return null;
    return {
        left: unescapeOperand(m[1]!).trim(),
        op: m[2] as "+" | "-",
        right: unescapeOperand(m[3]!).trim(),
    };
}

export function buildFormulaExpression(left: string, op: string, right: string): string {
    return `(${escapeOperand(left)})${op}(${escapeOperand(right)})`;
}

export function formatColumnDisplay(columnName: string): string {
    const formula = parseFormulaExpression(columnName);
    if (formula) return `${formula.left} ${formula.op} ${formula.right}`;
    return columnName;
}

export type TFormulaModalProps = {
    open: boolean;
    onClose: () => void;
    columns: string[];
    initialLeft: string;
    initialOp: "+" | "-";
    initialRight: string;
    onSubmit: (left: string, op: "+" | "-", right: string) => void;
};

export function FormulaModal({
    open,
    onClose,
    columns,
    initialLeft,
    initialOp,
    initialRight,
    onSubmit,
}: TFormulaModalProps) {
    const [left, setLeft] = useState(initialLeft);
    const [op, setOp] = useState<"+" | "-">(initialOp);
    const [right, setRight] = useState(initialRight);

    useEffect(() => {
        if (open) {
            setLeft(initialLeft);
            setOp(initialOp);
            setRight(initialRight);
        }
    }, [open, initialLeft, initialOp, initialRight]);

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            onClose();
        }
    };

    return (
        <Modal open={open} onOpenChange={handleOpenChange} title="Amount Header">
            <div className="space-y-4 py-2">
                <div className="flex items-center gap-4">
                    <label className="w-36 text-sm font-medium">Left Header:</label>
                    <select
                        value={left}
                        onChange={(e) => setLeft(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border rounded-md bg-background"
                    >
                        <option value="">Select...</option>
                        {columns.map((col) => (
                            <option key={col} value={col}>
                                {col}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-4">
                    <label className="w-36 text-sm font-medium">Operator:</label>
                    <select
                        value={op}
                        onChange={(e) => setOp(e.target.value as "+" | "-")}
                        className="flex-1 px-3 py-2 text-sm border rounded-md bg-background"
                    >
                        <option value="-">− (subtract)</option>
                        <option value="+">+ (add)</option>
                    </select>
                </div>

                <div className="flex items-center gap-4">
                    <label className="w-36 text-sm font-medium">Right Header:</label>
                    <select
                        value={right}
                        onChange={(e) => setRight(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border rounded-md bg-background"
                    >
                        <option value="">Select...</option>
                        {columns.map((col) => (
                            <option key={col} value={col}>
                                {col}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex gap-2 pt-2">
                <Button
                    onClick={() => {
                        if (left && right) {
                            onSubmit(left, op, right);
                            onClose();
                        }
                    }}
                    disabled={!left || !right}
                >
                    Submit
                </Button>
                <Button variant="outline" onClick={onClose}>
                    Cancel
                </Button>
            </div>
        </Modal>
    );
}
